import Image from "next/image";
import { useEffect, useState } from "react";

import styles from "@/styles/containers/cart.module.scss";

import { Button, Drawer } from "@/components";
import { Bag, CheckSuccess } from "@/components/Icons";

import { OrderStorage } from "@/storage/OrderStorage";

import { Brand } from "@/core/Brand";
import { Catalog } from "@/core/Catalog";

import { Alert } from "@/components/Alert";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";
import { EcleticaTable } from "@/services/Ecletica/EcleticaTable";
import { TableException } from "@/services/expections/TableException";
import { NetLogicTable } from "@/services/NetLogic";
import { CatalogHelpers, Currency } from "@/tools";
import Bugsnag from "@bugsnag/js";
import { useTranslations } from "next-intl";
import CartItem from "./Product/CartItem";
import ProductDetails from "./Product/ProductDetails";
import { ColibriTable } from "@/services/Colibri/ColibriTable";
import { ChallengeDrawer } from "./ChallengeDrawer";
import { SaiposTable } from "@/services/Saipos";

export default function Cart({ open, onClose }) {
  const [order, setOrder] = useState(OrderStorage.getOrder());
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [openProductDetails, setOpenProductDetails] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [cartItemIndex, setCartItemIndex] = useState(null);
  const [sendingOrder, setSendingOrder] = useState(false);
  const [sentOrder, setSentOrder] = useState(false);
  const [openConfirmSendOrder, setOpenConfirmSendOrder] = useState(false);
  const [openChallange, setOpenChallenge] = useState(false);

  useEffect(() => {
    if (open) setSentOrder(false);
  }, [open]);

  const cartItemsCount = order?.cart?.catalog?.items.reduce(
    (t, i) => t + (i.quantity || 1),
    0
  );

  const onEditProduct = (product, cartItemIndex) => {
    let productToUpdate = null;
    if (product.type === "pizza") {
      productToUpdate = Catalog.preparePizzaProductToUpdate(product);
    } else {
      productToUpdate = product;
    }

    setSelectedProduct(JSON.parse(JSON.stringify(productToUpdate)));
    setCartItemIndex(cartItemIndex);
    setOpenProductDetails(true);
  };

  const onOrderChanged = async () => {
    const order = OrderStorage.getOrder();
    setOrder(order);
  };

  useEffect(() => {
    window.addEventListener("orderUpdated", onOrderChanged);

    return () => {
      window.removeEventListener("orderUpdated", onOrderChanged);
    };
  }, []);

  const [appConfigurations] = useAppConfiguration();

  const onSendOrder = async () => {
    setSendingOrder(true);
    setOpenConfirmSendOrder(false);

    order.cart.catalog = CatalogHelpers.clearUnselectedItems(
      order.cart.catalog
    );

    const cartItems = order?.cart?.catalog?.items;
    if (cartItems?.length <= 0) {
      setError(t("emptyCart"));
      setSendingOrder(false);
      setShowError(true);
      return;
    }

    try {
      if (appConfigurations?.tableService === "ECLETICA") {
        const ecleticaTable = new EcleticaTable();
        await ecleticaTable.addItems(cartItems);
      } else if (appConfigurations?.tableService === "NETLOGIC") {
        const netlogicTable = new NetLogicTable();
        await netlogicTable.addItems(order);
      } else if (appConfigurations?.tableService === "COLIBRI") {
        const colibriTable = new ColibriTable();
        await colibriTable.addItems(order);
      } else if (appConfigurations?.tableService === "SAIPOS") {
        const saiposTable = new SaiposTable();
        await saiposTable.addItems(order);
      } else {
        console.error("Inclusão de itens da integração de mesa não implementada");
        return;
      }

      setError(null);
      setShowError(false);

      OrderStorage.clearCartItems();
      setSentOrder(true);
    } catch (err) {
      console.log(err);
      Bugsnag.notify(err);

      if (err instanceof TableException) {
        setError(t(err.template, err.variables));
        setSentOrder(false);
        setShowError(true);
        setSendingOrder(false);
        return;
      }

      setError(err.message || t("unableToAddItemsToTable"));
      setSentOrder(false);
      setShowError(true);
    }

    setSendingOrder(false);
  };

  const t = useTranslations();

  if (!order) {
    return null;
  }

  return (
    <Drawer
      open={open}
      onClose={() => !sendingOrder && onClose()}
      title={
        !sendingOrder
          ? sentOrder
            ? t("orderPlaced")
            : t("pendingItems")
          : null
      }
      withoutPadding
      direction="right"
    >
      {!sendingOrder && !sentOrder && (
        <div className={styles.cart}>
          {order?.cart?.catalog?.items.length > 0 ? (
            <div className={styles.scrollContainer}>
              <CartItem
                order={order}
                onEditProduct={onEditProduct}
                onRemoveProduct={(_, productIndex) => {
                  OrderStorage.removerCartItem(productIndex);
                }}
                onClose={() => onClose && onClose({ openCatalog: true })}
              />
            </div>
          ) : (
            <div className={styles.emptyOrder}>
              <Bag size={40} />
              <p>{t("noPendingItems")}</p>
              <span>{t("addItemsToSendToKitchen")}</span>
              <div className={styles.close}>
                <Button design="default" className="w-100" onClick={onClose}>
                  {t("close")}
                </Button>
              </div>
            </div>
          )}

          {order?.cart?.catalog?.items.length > 0 && (
            <div className={styles.footer}>
              <>
                <div className={styles.totals}>
                  <span>Subtotal</span>
                  <p>{Currency.formatCurrency(order?.total)}</p>
                </div>
                <Button
                  design="secondary"
                  className="w-100"
                  onClick={() => setOpenConfirmSendOrder(true)}
                  disabled={cartItemsCount === 0}
                >
                  {t("sendToKitchen")}
                </Button>
              </>
            </div>
          )}
        </div>
      )}

      {sendingOrder && (
        <div className={styles.sendingOrder}>
          <div className={styles.brandHeader}>
            {Brand.avatar && (
              <div className={styles.picture}>
                <Image src={Brand.avatar} alt={Brand.name} fill />
              </div>
            )}
          </div>

          {!sentOrder && (
            <>
              <div className={styles.statusContainer}>
                <p className={styles.msgOne}>{t("receivingYourOrder")}</p>
                <p className={styles.msgTwo}>{t("pleaseWaitAlmostThere")}</p>
              </div>

              <span className={styles.loader} />
            </>
          )}
        </div>
      )}

      <Drawer
        open={openConfirmSendOrder}
        alreadyDrawer
        direction="bottom-right"
      >
        <p className={styles.alertTitle}>{t("confirmSendItemsToKitchen")}</p>
        <Button 
          design="primary" 
          className="w-100" 
          onClick={() => {
            setOpenConfirmSendOrder(false);

            if (appConfigurations?.allowChallenge === true) {
              setOpenChallenge(true);
            } else {
              onSendOrder();
            }
          }}
        >
          {t("confirm")}
        </Button>
        <Button
          design="secondary"
          className="mt-10 w-100"
          onClick={() => setOpenConfirmSendOrder(false)}
        >
          {t("cancel")}
        </Button>
      </Drawer>

      {sentOrder && (
        <div className={styles.sentOrder}>
          <div className={styles.success}>
            <CheckSuccess />
          </div>
          <span>{t("orderSentToKitchen")}</span>
          <div className={styles.close}>
            <Button design="default" className="w-100" onClick={onClose}>
              {t("close")}
            </Button>
          </div>
        </div>
      )}

      <Alert
        open={showError}
        onClose={() => setShowError(false)}
        title={t("tryAgain")}
        subtitle={error}
      />

      <Drawer
        open={openProductDetails}
        direction="center-full"
        onClose={() => setOpenProductDetails(false)}
        withoutPadding
        alreadyDrawer
      >
        <ProductDetails
          cartItemIndex={cartItemIndex}
          product={selectedProduct}
          onClose={() => setOpenProductDetails(false)}
        />
      </Drawer>

      {openChallange && (
        <ChallengeDrawer
          onClose={() => {
            setOpenChallenge(false);
          }}
          onConfirm={() => {
            setOpenChallenge(false);
            onSendOrder();
          }}
        />
      )}
    </Drawer>
  );
}
